import { useContext, useEffect, useState } from "react";
import fetchStatus from "src/utils/api/fetch-status";
import { ActiveDate } from "src/scenes/dashboard/date-provider";
import { addDays, format } from "date-fns";
import { useAuth } from "src/components/providers/auth-context";
import { useActiveIntegrations } from "../active-integrations-provider";
import useAbortableStreamFetch from "../../../utils/request/use-abortable-stream-fetch";

const useDashboardApi = (
    requestMethodMock,
    requestMethodActual,
    requiredIntegrations,
    streamURL,
    options,
    queryParams
) => {
    const { fromDate, toDate } = useContext(ActiveDate);
    const { activeIntegrations } = useActiveIntegrations();
    const { isDemo, isUserPayingCustomer } = useAuth();

    const [state, setState] = useState({
        status: fetchStatus.PENDING,
        data: null,
        errorCode: null,
        string: "",
    });

    const fetchFromApi = async (requestMethod, controller) => {
        try {
            const response = await requestMethod({
                fromDate: fromDate && format(fromDate, "yyyy-MM-dd"),
                toDate: toDate && format(addDays(toDate, 1), "yyyy-MM-dd"),
                queryParams,
                abortController: controller,
            });
            setState((prevState) => ({
                ...prevState,
                status: fetchStatus.SUCCESS,
                data: response,
                errorCode: null,
                controller: null,
            }));
        } catch (e) {
            setState((prevState) => ({
                ...prevState,
                status: fetchStatus.ERROR,
                data: null,
                errorCode: e.status,
                controller: null,
            }));
        }
    };

    // eslint-disable-next-line consistent-return
    const fetchEffect = (controller) => {
        const actualController = controller || new AbortController();
        setState({
            status: fetchStatus.PENDING,
            data: null,
            errorCode: null,
            string: "",
            controller: actualController,
        });

        if (process.env.MOCK_WORK === "true" || isDemo || !isUserPayingCustomer)
            return fetchFromApi(requestMethodMock, actualController);
        if (activeIntegrations.status === fetchStatus.SUCCESS) {
            if (!activeIntegrations.data.integrations.find((int) => int.connected))
                return fetchFromApi(requestMethodMock, actualController);
            if (requiredIntegrations) {
                const shouldRequest = !!activeIntegrations.data.integrations.find(
                    (int) => int.connected && requiredIntegrations.includes(int.name)
                );
                if (!shouldRequest) return undefined;
            }
            if (activeIntegrations.data.integrations.find((int) => int.connected)) {
                if (streamURL) {
                    setState((prevState) => ({
                        ...prevState,
                        data: null,
                        string: "",
                    }));
                    useAbortableStreamFetch(
                        streamURL,
                        state,
                        actualController,
                        setState,
                        fromDate,
                        toDate,
                        options,
                        queryParams
                    );
                } else {
                    fetchFromApi(requestMethodActual, actualController);
                }
            } else {
                fetchFromApi(requestMethodMock, actualController);
            }
        }
    };

    const refetch = (controller) => {
        if (state.controller) {
            state.controller.abort("unmounted");
        }
        setState((prevState) => ({
            ...prevState,
            status: fetchStatus.REFETCHING,
            controller,
        }));
        return fetchEffect(controller);
    };

    useEffect(() => {
        const controller = new AbortController();
        refetch(controller);
        return () => {
            if (controller) {
                controller.abort("unmounted");
            }
        };
    }, [
        activeIntegrations &&
            activeIntegrations.data &&
            activeIntegrations.data.integrations &&
            JSON.stringify(activeIntegrations.data.integrations),
        fromDate,
        toDate,
        streamURL,
        queryParams,
        options && JSON.stringify(options),
    ]);

    return {
        ...state,
        refetch,
    };
};

export default useDashboardApi;
